import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, CreditCard, Mail, User, LogOut, Sparkles, TrendingDown } from "lucide-react";
import AppLayout from "../components/AppLayout";
import { useAsaAuth } from "../AuthContext";
import { tierById, tierPrice, gbp, hasFullAccess, TIERS } from "../data";
import { startSubscriptionCheckout } from "../payments";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CANCEL_REASONS = [
  "Too expensive",
  "Not using it enough",
  "Missing a feature I need",
  "Found an alternative",
  "Just taking a break",
  "Other",
];

export default function Account() {
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAsaAuth();
  const [confirming, setConfirming] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState("");
  const [reasonDetail, setReasonDetail] = useState("");
  const tier = tierById(user?.tier || "essential");
  const billing = user?.billing || "monthly";
  const price = tierPrice(tier.id, billing);
  const isPro = hasFullAccess(user);
  const cancelled = isPro && user?.cancel_requested;
  const accessUntil = user?.access_until ? String(user.access_until).slice(0, 10) : null;
  const proTier = TIERS.find((t) => t.id === "pro");
  const showSaveOffer = reason === "Too expensive" && billing !== "yearly";

  const switchToYearly = async () => {
    setCancelling(true);
    try {
      await startSubscriptionCheckout({ tier: "pro", billing: "yearly", kind: "upgrade", email: user.email });
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setCancelling(false);
    }
  };

  const doCancel = async () => {
    setCancelling(true);
    try {
      const { data } = await axios.post(`${API}/account/cancel-subscription`, {
        reason: reason || null,
        reason_detail: reason === "Other" ? reasonDetail : null,
      });
      await refreshUser();
      setConfirming(false);
      setReason("");
      setReasonDetail("");
      toast.success(data.message || "Your membership has been cancelled.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel. Please try again.");
    }
    setCancelling(false);
  };
  const doLogout = () => { logout(); navigate("/asa"); };

  return (
    <AppLayout title="Account">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h2 className="font-head font-semibold text-slate-800 flex items-center gap-2"><User size={18} className="text-[#0d9488]" /> Profile</h2>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <div><p className="text-[12px] text-slate-400">Name</p><p className="font-semibold text-slate-700">{user?.name}</p></div>
              <div><p className="text-[12px] text-slate-400">Email</p><p className="font-semibold text-slate-700">{user?.email}</p></div>
              <div><p className="text-[12px] text-slate-400">Member since</p><p className="font-semibold text-slate-700">{user?.since}</p></div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="account-membership">
            <h2 className="font-head font-semibold text-slate-800 flex items-center gap-2"><CreditCard size={18} className="text-[#0d9488]" /> Membership</h2>
            <div className="flex items-center justify-between mt-4 bg-[#e6f4f2] rounded-xl p-4">
              <div>
                <p className="font-semibold text-[#0f766e] flex items-center gap-1.5" data-testid="account-tier-name"><ShieldCheck size={16} /> {isPro ? "ASAL Pro" : "ASAL Essential (Free)"} plan</p>
                <p className="text-[13px] text-slate-500 mt-0.5">{isPro ? `${tier.tagline} · billed ${billing}` : "Free forever · awareness & basic recording"}</p>
              </div>
              <span className="text-xl font-head font-bold text-slate-800">{isPro ? <>{gbp(price)}<span className="text-[12px] font-normal text-slate-400">/{billing === "yearly" ? "yr" : "mo"}</span></> : "Free"}</span>
            </div>

            {cancelled && (
              <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="cancel-status">
                <p className="text-[13.5px] text-slate-700 font-semibold">Membership cancelled — won't renew</p>
                <p className="text-[13px] text-slate-500 mt-1 leading-relaxed">You keep full ASAL Pro access{accessUntil ? <> until <strong>{accessUntil}</strong></> : ""}, then your account moves to ASAL Essential (free). Changed your mind? You can re-subscribe any time.</p>
                <button onClick={() => navigate("/asa/pricing")} data-testid="resubscribe-btn" className="mt-3 h-10 px-4 rounded-lg bg-[#0d9488] hover:bg-[#0f766e] text-white font-semibold text-sm transition-colors">Re-subscribe</button>
              </div>
            )}

            {isPro && !cancelled ? (
              <div className="flex flex-wrap gap-3 mt-4">
                <button onClick={() => navigate("/asa/pricing")} className="h-10 px-4 rounded-lg bg-[#0d9488] hover:bg-[#0f766e] text-white font-semibold text-sm transition-colors">Manage plan</button>
                <button onClick={() => setConfirming(true)} data-testid="cancel-membership-btn" className="h-10 px-4 rounded-lg border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50 transition-colors">Cancel membership</button>
              </div>
            ) : !isPro ? (
              <div className="mt-4">
                <button onClick={() => navigate("/asa/pricing")} data-testid="upgrade-pro-btn" className="h-10 px-4 rounded-lg bg-[#0d9488] hover:bg-[#0f766e] text-white font-semibold text-sm transition-colors">Upgrade to ASAL Pro</button>
              </div>
            ) : null}

            {confirming && !cancelled && (
              <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="cancel-confirm">
                <p className="text-[13.5px] text-amber-900 font-semibold">Cancel your ASAL Pro membership?</p>
                <p className="text-[13px] text-amber-800 mt-1 leading-relaxed">Your membership won't renew. Because you paid for your term upfront, you'll <strong>keep ASAL Pro until your paid period ends</strong>, then move to ASAL Essential (free). You can re-subscribe any time.</p>

                <p className="text-[12.5px] font-semibold text-amber-900 mt-3 mb-1.5">Mind telling us why? (optional)</p>
                <div className="flex flex-wrap gap-2" data-testid="cancel-reasons">
                  {CANCEL_REASONS.map((r) => (
                    <button key={r} type="button" onClick={() => setReason(r)} data-testid={`cancel-reason-${r.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                      className={`text-[12.5px] px-3 py-1.5 rounded-full border transition-colors ${reason === r ? "bg-amber-500 border-amber-500 text-white" : "bg-white border-amber-200 text-amber-800 hover:border-amber-400"}`}>{r}</button>
                  ))}
                </div>
                {reason === "Other" && (
                  <textarea value={reasonDetail} onChange={(e) => setReasonDetail(e.target.value)} data-testid="cancel-reason-detail" rows={2} placeholder="Tell us a little more…" className="w-full mt-2 px-3 py-2 rounded-lg border border-amber-200 text-sm outline-none focus:border-amber-400 resize-none bg-white" />
                )}

                {showSaveOffer && (
                  <div className="mt-3 rounded-xl border-2 border-[#0d9488] bg-[#e6f4f2] p-4" data-testid="save-offer">
                    <p className="text-[13.5px] font-bold text-[#0f766e] flex items-center gap-1.5"><Sparkles size={16} /> Wait — here's a better deal</p>
                    <p className="text-[13px] text-slate-600 mt-1 leading-relaxed">Switch to <strong>yearly</strong> and get <strong>2 months free</strong> — just <strong>{gbp(proTier.yearly / 12)}/month</strong> ({gbp(proTier.yearly)} billed once a year) instead of {gbp(proTier.monthly)}/month. Keep everything you have.</p>
                    <button onClick={switchToYearly} disabled={cancelling} data-testid="save-offer-yearly-btn" className="mt-3 h-10 px-4 rounded-lg bg-[#0d9488] hover:bg-[#0f766e] disabled:opacity-60 text-white font-semibold text-sm inline-flex items-center gap-2 transition-colors"><TrendingDown size={16} /> Switch to yearly & save</button>
                  </div>
                )}

                <div className="flex flex-wrap gap-3 mt-3">
                  <button onClick={doCancel} disabled={cancelling} data-testid="cancel-confirm-btn" className="h-10 px-4 rounded-lg bg-rose-600 hover:bg-rose-700 disabled:opacity-60 text-white font-semibold text-sm transition-colors">{cancelling ? "Cancelling…" : showSaveOffer ? "No thanks, cancel anyway" : "Yes, cancel membership"}</button>
                  <button onClick={() => { setConfirming(false); setReason(""); setReasonDetail(""); }} disabled={cancelling} className="h-10 px-4 rounded-lg border border-slate-300 text-slate-600 font-semibold text-sm hover:bg-white transition-colors">Keep my membership</button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-5">
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="font-head font-semibold text-slate-800 flex items-center gap-2"><Mail size={17} className="text-[#0d9488]" /> Data & privacy</h3>
            <p className="text-[13px] text-slate-500 mt-2 leading-relaxed">Your app data (checker results, diary entries, reports) is stored securely in your ASAL account. It is not emailed anywhere.</p>
          </div>
          <button onClick={doLogout} className="w-full h-11 rounded-lg border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-red-50 hover:text-red-600 transition-colors inline-flex items-center justify-center gap-2"><LogOut size={17} /> Log out</button>
          <p className="text-[11px] text-slate-400 text-center leading-relaxed">© {new Date().getFullYear()} ASA Foundation. All rights reserved.<br />Copyright &amp; IP: Asal Shirazi.</p>
        </div>
      </div>
    </AppLayout>
  );
}
